import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { memo, useMemo } from 'react'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Expand01Icon, MoveIcon, ThumbsUpIcon } from '@/components/ui/icons'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types.ts'
import { useUpdateExperienceVettingImageIsApproved } from '@/features/experience-vetting/queries/use-update-experience-vetting-image-is-approved.ts'
import {
	OpsVettingDisabledContainer,
	OpsVettingDragButton,
	OpsVettingGalleryItemsContainer,
	OpsVettingImageTitle
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'
import { ExperienceVettingGalleryImageRejectDialog } from './experience-vetting-gallery-image-reject-dialog'

export const ExperienceVettingCriteriaGalleryItem = memo(
	({
		image,
		hideAction,
		experienceId,
		galleryId,
		onClickExpand
	}: {
		image: ExperienceGalleryImage
		hideAction?: boolean
		experienceId: number
		galleryId: number
		onClickExpand?: () => void
	}) => {
		const { t } = useTranslation()

		const updateExperienceVettingImageIsApproved =
			useUpdateExperienceVettingImageIsApproved({
				experienceId,
				galleryId,
				imageId: image.id
			})

		const {
			attributes,
			setNodeRef,
			transform,
			transition,
			isDragging,
			listeners
		} = useSortable({ id: image.id })

		const style = {
			transform: CSS.Translate.toString(transform),
			transition
		}

		const isApproved = useMemo(
			() => image.isApproved === true,
			[image.isApproved]
		)

		return (
			<OpsVettingDisabledContainer
				$isDisabled={isDragging}
				ref={setNodeRef}
				style={style}
				{...attributes}
			>
				<OpsVettingGalleryItemsContainer direction='column' gap={4}>
					<GalleryImageItem
						bottomRightSlot={
							!hideAction && (
								<ButtonLegacy
									className='show-on-hover'
									onClick={onClickExpand}
									shape='square'
									size='sm'
									type='button'
									variant='secondary'
								>
									<Expand01Icon />
								</ButtonLegacy>
							)
						}
						src={image.thumbnails?.xs || image.url}
						topLeftSlot={
							hideAction ? null : (
								<OpsVettingDragButton
									$isDragging={isDragging}
									{...listeners}
									className='show-on-hover'
									shape='square'
									size='sm'
									type='button'
									variant='secondary'
								>
									<MoveIcon size={16} />
								</OpsVettingDragButton>
							)
						}
					/>
					<div className='flex w-full flex-col gap-2'>
						<OpsVettingImageTitle>
							{image.name}
						</OpsVettingImageTitle>
						<div className='flex w-full gap-3'>
							<ButtonLegacy
								className='w-full'
								disabled={hideAction}
								onClick={() =>
									!isApproved &&
									updateExperienceVettingImageIsApproved.mutate(
										{ isApproved: true }
									)
								}
								size={'sm'}
								type='button'
								variant={
									isApproved ? 'primarySuccess' : 'secondary'
								}
							>
								<ThumbsUpIcon />
								{t('vetting:criteria.service.gallery.accept')}
							</ButtonLegacy>
							<ExperienceVettingGalleryImageRejectDialog
								currentFlags={image.flags}
								disabled={hideAction}
								experienceId={experienceId}
								galleryId={galleryId}
								image={image}
							/>
						</div>
					</div>
				</OpsVettingGalleryItemsContainer>
			</OpsVettingDisabledContainer>
		)
	}
)
